import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, debounce, Popover, Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import DateRangeFilter from '../DateRangeFilter/DateRangeFilter';
import MultiSelectPopover from '../MultiSelectPopover/MultiSelectPopover';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'; // Import the dropdown icon
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAggregation,
  selectChannelKeys,
  selectClientType,
  selectEndDate,
  selectFilterAccount,
  selectLocationKeys,
  selectMachineTypeKeys,
  selectProductKeys,
  selectSelectedClientConfig,
  selectStartDate,
  selectTimeStamp,
} from 'src/app/selectors/filterSelectors';
import {
  getActiveUsers,
  getInterActionsData,
  getInventoryData,
  getNetOpsData,
  getReportsData,
  getTransactionData,
  resetSelectedFilter,
  updateSelectedFilter,
} from 'src/slices/filterSlice';
import { formatEndDate, getDateRange } from 'src/utils/DateUtil';
import { useConfig } from 'src/context/ConfigContext';
import {
  getUrlForReports,
} from 'src/utils/DataMappings';
import { selectReport, selectTile } from 'src/app/selectors/appDataSelectors';
import { AppConfig } from 'src/config/AppConfig';
import OperatingHoursPicker from '../OperatingHoursPicker/OperatingHoursPicker';

// Define styles using makeStyles
const useStyles = makeStyles((theme: Theme) => ({
  filterBar: {
    display: 'flex',
    alignItems: 'end',
    gap: theme.spacing(2),
    padding: 2,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
  },
  button: {
    height: '40px',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.getContrastText(theme.palette.primary.main),
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  formControl: {
    minWidth: 150,
  },
}));

const FilterBar: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { config } = useConfig();
  const clientType = useSelector(selectClientType);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorOpHrsEl, setAnchorOpHrsEl] = useState<null | HTMLElement>(null);
  const channels = useSelector(selectChannelKeys);
  const machineTypes = useSelector(selectMachineTypeKeys);
  const locationKeys = useSelector(selectLocationKeys);
  const products = useSelector(selectProductKeys);
  const [selectedRange, setSelectedRange] = useState<string>('Last 30 Days');
  const [showFilterButtons, setshowFilterButtons] = useState<boolean>(false);
  const startDate = useSelector(selectStartDate);
  const aggregation = useSelector(selectAggregation);
  const endDate = useSelector(selectEndDate);
  const timeStamp = useSelector(selectTimeStamp);
  const selectedMenu = useSelector(selectReport);
  const clientConfig = useSelector(selectSelectedClientConfig);
  const reportTile = localStorage.getItem('reportTile') || 'Transactions';
  const isHide =
    selectedMenu === 'admin' ||
    localStorage.getItem('selectedMenu') === 'admin' ||
    selectedMenu === 'users' ||
    localStorage.getItem('selectedMenu') === 'users';
  
  const [isOperatingHoursPickerOpen, setOperatingHoursPickerOpen] =
    useState<boolean>(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [filterData, setFilterData] = useState('');

  const [selectedFilters, setSelectedFilters] = useState({
    dateRange: 'Last 30 Days',
    channel: [] as string[],
    machine_type: [] as string[],
    store: locationKeys as string[],
    operating_hours: 'All Day',
    products: products as string[],
    storeStatus: '',
    groupBy:
      AppConfig.reportGroupBy[reportTile].length > 0
        ? AppConfig.reportGroupBy[reportTile][0]
        : '',
    aggregation:
      AppConfig.reportAggregation[reportTile].length > 0
        ? AppConfig.reportAggregation[reportTile][0]
        : '',
  });
  const [timeRangeForNetOps, setTimeRangeForNetOps] = useState(
    AppConfig.allDay,
  );
  const toggleOperatingHoursPicker = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorOpHrsEl(event.currentTarget);
    setOperatingHoursPickerOpen(!isOperatingHoursPickerOpen);
  };

  // Use the styles created
  const classes = useStyles();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setAnchorOpHrsEl(null);
    setOperatingHoursPickerOpen(!isOperatingHoursPickerOpen);
  };

  const handleApplyDateRange = (
    startDate: Date,
    endDate: Date,
    timeStamp: string,
    range: string,
  ) => {
    console.log('---range-', range);
    setSelectedRange(
      range.includes('CUSTOM')
        ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
        : range,
    );
    dispatch(
      updateSelectedFilter({
        key: 'timeStamp',
        value: timeStamp,
      }),
    );
    dispatch(
      updateSelectedFilter({
        key: 'start',
        value: formatEndDate(startDate),
      }),
    );
    dispatch(
      updateSelectedFilter({
        key: 'end',
        value: formatEndDate(endDate),
      }),
    );
    handleClose();
  };

  const open = Boolean(anchorEl);
  const updateFilter = useCallback(
    debounce((key: string, value: string[]) => {
      if (key === 'groupBy') {
        value = value.map((item) => AppConfig.reportGroupById[item]);
      }
      dispatch(updateSelectedFilter({ key, value: value ? value.join(',') : '' }));
    }, 300),
    [],
  );
  const handleSelectChange = (
    key: keyof typeof selectedFilters,
    data: string[],
  ) => {
    setSelectedFilters((prev) => ({ ...prev, [key]: data }));
    updateFilter(key, data);
    if (hasInteracted && data.length > 0) setFilterData(filterData + key);
    else setFilterData(filterData.replace(key, ''));
  };
  useEffect(() => {
    if (filterData.length > 0 && hasInteracted)
      setshowFilterButtons(true); // Set to true if filters have changed
    else if (filterData.length == 0 && hasInteracted)
      setshowFilterButtons(false);
  }, [selectedFilters, hasInteracted]);

  const handleApply = () => {
    // This function will call the API once the startDate and endDate have been updated in Redux
    if (!config) {
      console.error('no Config loaded');
      return;
    }
    if (startDate && endDate) {
      // Your API call here
      console.log(
        'Calling API with updated date range:',
        startDate,
        endDate,
        config,
      );
      const headers = {
        Authorization: config['HEADER']['Authorization'],
      };
      if (selectedMenu === 'transactions') {
        dispatch(
          getTransactionData({
            url: config.API_URL.dash_transactions,
            headers,
            type: '',
          }),
        );
      } else if (selectedMenu === 'interactions') {
        dispatch(
          getInterActionsData({
            url: config.API_URL.dash_interactions,
            headers,
            type: '',
          }),
        );
      } else if (selectedMenu === 'netOps') {
        dispatch(
          getNetOpsData({ url: config.API_URL.net_ops, headers, type: '' }),
        );
      } else if (selectedMenu === 'inventory') {
        dispatch(
          getInventoryData({
            url: config.API_URL.inventory,
            headers,
            page: 1,
            selectedMenu: selectedMenu,
          }),
        );
      } else if (selectedMenu === 'stores') {
        //store_status
        dispatch(
          getInventoryData({
            url: config.API_URL.store_status,
            headers,
            page: 1,
            selectedMenu: selectedMenu,
          }),
        );
      } else if (selectedMenu === 'users' || selectedMenu === 'admin') {
        //store_status
        dispatch(getActiveUsers({ selectedMenu: selectedMenu }));
      } else if (selectedMenu === 'reports') {
        //store_status
        dispatch(
          getReportsData({
            url: getUrlForReports(
              config.API_URL,
              localStorage.getItem('reportTile') || AppConfig.reports[0],
            ),
            headers,
            page: 1,
            selectedMenu: selectedMenu,
            selectedTile:
              localStorage.getItem('reportTile') || AppConfig.reports[0],
          }),
        );
      }
    }
  };

  useEffect(() => {
    // Check when the startDate and endDate in Redux have been updated, then call the API
    console.log(' API with updated:', startDate, endDate, selectedMenu);
    if (startDate && endDate && selectedMenu) {
      handleApply(); // Call API only when dates are updated
    }
  }, [startDate, endDate, selectedMenu, aggregation, config, clientConfig]);

  useEffect(() => {
    const { startDate, endDate } = getDateRange('last 30 days');
    dispatch(updateSelectedFilter({ key: 'start', value: startDate }));
    dispatch(
      updateSelectedFilter({
        key: 'end',
        value: endDate + 'T23:59:59',
      }),
    );
    dispatch(
      updateSelectedFilter({
        key: 'report',
        value: selectedMenu || 'transactions',
      }),
    );
  }, []);

  const handleApplyOperatingHoursPicker = (value: {
    name: 'allDay' | 'businessHours' | 'custom';
    from: 'string';
    to: 'string';
  }) => {
    if (value.name === 'custom') {
      setTimeRangeForNetOps({
        name: value.from + ' - ' + value.to,
        from: value.from,
        to: value.to,
      });
    } else {
      setTimeRangeForNetOps(value);
    }
    let to = value.to.replace(':00', ':59');
    if (value.to.includes('24')) {
      to = '23:59';
    }
    dispatch(
      updateSelectedFilter({
        key: 'operating_hours',
        value: value.from + ':00' + ',' + to + ':00',
      }),
    );
    setHasInteracted(true);
    handleClose();
  };
  const handleReset = () => {
    setSelectedFilters({
      dateRange: 'Last 30 Days',
      channel: [],
      machine_type: [],
      store: [],
      operating_hours: AppConfig.allDay.name,
      products: '',
      storeStatus: '',
      groupBy:
        AppConfig.reportGroupBy[reportTile].length > 0
          ? AppConfig.reportGroupBy[reportTile][0]
          : '',
      aggregation:
        AppConfig.reportAggregation[reportTile].length > 0
          ? AppConfig.reportAggregation[reportTile][0]
          : '',
    });
    setshowFilterButtons(false);
    setHasInteracted(false);
    dispatch(resetSelectedFilter());
  };

  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // month is zero-indexed
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  return (
    <>
      {isHide ? (
        <></>
      ) : (
        <Box className={classes.filterBar} sx ={{fontSize : '.9rem'}}>
          <Button
            onClick={handleClick}
            variant="outlined"
            className={classes.button}
            color={theme.palette.primary.contrastText}
            endIcon={<ExpandMoreIcon className={open ? 'rotate-180' : ''} />}
            sx={{textTransform: 'capitalize'}}
          >
            {selectedRange}
          </Button>

          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <DateRangeFilter
              onApply={handleApplyDateRange}
              timeStamp={timeStamp || 'storeLocal'}
            />
          </Popover>
          {selectedMenu === 'netOps' && (
            <Button
              onClick={toggleOperatingHoursPicker}
              variant="outlined"
              color={theme.palette.primary.contrastText}
              className={classes.button}
              sx={{textTransform: 'capitalize'}}
            >
              Operating Hours:{timeRangeForNetOps.name}
            </Button>
          )}

          {selectedMenu === 'netOps' && isOperatingHoursPickerOpen && (
            <Popover
              open={isOperatingHoursPickerOpen}
              anchorEl={anchorOpHrsEl}
              onClose={toggleOperatingHoursPicker}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              <OperatingHoursPicker
                handleClose={handleClose}
                onApply={handleApplyOperatingHoursPicker}
              />
            </Popover>
          )}

          {channels && channels.length > 0 && (
            <Box>
              Channels
              <MultiSelectPopover
                options={channels}
                label="Search Channels"
                buttonLabel={
                  <span style={{ textTransform: 'capitalize' }}>
                    {selectedFilters.channel.length ? selectedFilters.channel.join(', ') : 'All'}
                  </span>
                }
                selectedOptions={selectedFilters.channel}
                isMultiSelect={true}
                onSelectChange={(data) =>
                  handleSelectChange('channel', data as string[])
                }
                handleMultiClick={() => setHasInteracted(true)}
                onApply={handleApply}
              />
            </Box>
          )}
          {machineTypes && machineTypes.length > 0 && (
            <Box>
              Store Types
              <MultiSelectPopover
                options={machineTypes}
                label="Store Types"
                buttonLabel={
                  <span style={{ textTransform: 'capitalize' }}>
                    {selectedFilters.machine_type.length ? selectedFilters.machine_type.join(', ') : 'All'}
                  </span>
                }
                isMultiSelect={true}
                selectedOptions={selectedFilters.machine_type}
                onSelectChange={(data) =>
                  handleSelectChange('machine_type', data as string[])
                }
                handleMultiClick={() => setHasInteracted(true)}
                onApply={handleApply}
              />
            </Box>
          )}
          {selectedMenu === 'stores' && AppConfig.storeStatus.length > 0 && (
            <Box>
              Store Status
              <MultiSelectPopover
                options={AppConfig.storeStatus}
                label="Search"
                buttonLabel={
                  <span style={{ textTransform: 'capitalize' }}>
                    {selectedFilters.storeStatus.length ? selectedFilters.storeStatus.join(', ') : 'All'}
                  </span>
                }
                isMultiSelect={true}
                selectedOptions={selectedFilters.store}
                onSelectChange={(data) =>
                  handleSelectChange('storeStatus', data as string[])
                }
                handleMultiClick={() => setHasInteracted(true)}
                onApply={handleApply}
              />
            </Box>
          )}
          {locationKeys && locationKeys.length > 0 && (
            <Box>
              Stores
              <MultiSelectPopover
                options={locationKeys}
                label="Search"
                buttonLabel={
                  <span style={{ textTransform: 'capitalize' }}>
                    {selectedFilters.store.length ? selectedFilters.store.join(', ') : 'All'}
                  </span>
                }
                isMultiSelect={true}
                selectedOptions={selectedFilters.store}
                onSelectChange={(data) =>
                  handleSelectChange('store', data as string[])
                }
                handleMultiClick={() => setHasInteracted(true)}
                onApply={handleApply}
              />
            </Box>
          )}
          {selectedMenu === 'inventory' && products && products.length > 0 && (
            <Box>
              Products
              <MultiSelectPopover
                options={products}
                label="Search"
                buttonLabel={
                  <span style={{ textTransform: 'capitalize' }}>
                    {selectedFilters.products.length ? selectedFilters.products.join(', ') : 'All'}
                  </span>
                }
                isMultiSelect={true}
                selectedOptions={selectedFilters.products}
                onSelectChange={(data) =>
                  handleSelectChange('products', data as string[])
                }
                handleMultiClick={() => setHasInteracted(true)}
                onApply={handleApply}
              />
            </Box>
          )}

          {selectedMenu === 'reports' &&
            AppConfig.reportGroupBy[reportTile].length > 0 && (
              <Box>
                GroupBy
                <MultiSelectPopover
                  options={AppConfig.reportGroupBy[reportTile]}
                  label="Search"
                  buttonLabel={
                    <span style={{ textTransform: 'capitalize' }}>
                      {selectedFilters.groupBy}
                    </span>
                  }
                  //buttonLabel={selectedFilters.groupBy}
                  isMultiSelect={false}
                  isAll={false}
                  selectedOptions={selectedFilters.groupBy}
                  onSelectChange={(data) =>
                    handleSelectChange('groupBy', [data] as string[])
                  }
                  handleMultiClick={() => setHasInteracted(true)}
                  onApply={handleApply}
                />
              </Box>
            )}
          {selectedMenu === 'reports' &&
            AppConfig.reportAggregation[reportTile].length > 0 && (
              <Box>
                Aggregation
                <MultiSelectPopover
                  options={AppConfig.reportAggregation[reportTile]}
                  label="Search"
                  buttonLabel={selectedFilters.aggregation}
                  isMultiSelect={false}
                  isAll={false}
                  selectedOptions={selectedFilters.aggregation}
                  onSelectChange={(data) =>
                    handleSelectChange('aggregation', [data] as string[])
                  }
                  handleMultiClick={() => setHasInteracted(true)}
                  onApply={handleApply}
                />
              </Box>
            )}

          {showFilterButtons && (
            <>
              <Button
                onClick={handleApply}
                variant="contained"
                className={classes.button}
                
              >
                Apply
              </Button>

              <Button
                onClick={handleReset}
                variant="outlined"
                className={classes.button}
              >
                Reset
              </Button>
            </>
          )}
          <Box style={{ display: 'flex', justifyContent: 'flex-end', width: '60%' }}>
            <div style={{ fontSize: '12px' }}>
              {'Data Last Updated: '} {getCurrentDateTime()}
            </div>
          </Box>
        </Box>
      )}
    </>
  );
};

export default FilterBar;
